import { AccountDateTimePicker as DateTimePicker } from '../date-time-picker';
/**
 * Recurrence editor (Outlook "Make recurring" / Google "Custom"): frequency, every N days/weeks/months, weekdays,
 * monthly on a date / the nth weekday / the last day, yearly on a date / the nth weekday of a month, and the end
 * (never, on a date, after N times). The value is the API recurrence; its start date follows the event start on save.
 */
import { useI18n } from '@platform/i18n';
import { Button, Input, Label, MenuSelect, SegmentedControl } from '@platform/ui';
import { useId } from 'react';
import { useCalendarFormat } from './format';
import { defaultRule, isoDate, nthOf, recurrenceText, WEEKDAYS, WORKDAYS, withoutEnd, type Frequency, type Recurrence, type WeekIndex } from './recurrence';

type MonthlyMode = 'date' | 'nth' | 'last' | 'lastDay';
type EndMode = 'never' | 'date' | 'count';


const frequencyOf = (rule: Recurrence | null): Frequency => {
  if (!rule) return 'none';
  if (rule.pattern === 'weekly' && rule.interval === 1 && rule.daysOfWeek?.length === 5 && WORKDAYS.every((d) => rule.daysOfWeek?.includes(d))) return 'weekdays';
  return rule.pattern === 'other' ? 'none' : rule.pattern;
};

const monthlyModeOf = (rule: Recurrence): MonthlyMode => (rule.weekIndex ? (rule.dayKind === 'day' ? 'lastDay' : rule.weekIndex === 'last' ? 'last' : 'nth') : 'date');

export function RecurrenceEditor({ value, onChange, start, disabled }: { value: Recurrence | null; onChange: (rule: Recurrence | null) => void; start: Date; disabled?: boolean }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const ids = { interval: useId(), until: useId(), count: useId() };
  const frequency = frequencyOf(value);
  const day = WEEKDAYS[start.getDay()] ?? 'monday';
  const nth = nthOf(start);
  const dayName = (d: (typeof WEEKDAYS)[number]) => new Intl.DateTimeFormat(format.locale, { weekday: 'long' }).format(new Date(2026, 0, 4 + WEEKDAYS.indexOf(d)));
  const monthName = new Intl.DateTimeFormat(format.locale, { month: 'long' }).format(start);
  const index = (i: WeekIndex) => t(`webmail.calendarUx.recurrence.index.${i}`);
  const set = (patch: Partial<Recurrence>) => value && onChange({ ...value, ...patch });

  const unitKey = { daily: 'webmail.calendarUx.recurrence.days', weekly: 'webmail.calendarUx.recurrence.weeks', monthly: 'webmail.calendarUx.recurrence.months' } as const;
  const endMode: EndMode = value?.endDate ? 'date' : value?.count ? 'count' : 'never';

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">{t('webmail.calendar.editor.repeat')}</span>
        <MenuSelect
          label={t('webmail.calendar.editor.repeat')}
          value={frequency}
          disabled={disabled}
          options={(['none', 'daily', 'weekdays', 'weekly', 'monthly', 'yearly'] as const).map((f) => ({ value: f, label: t(`webmail.calendar.editor.repeats.${f}`) }))}
          onChange={(f) => onChange(defaultRule(f, start))}
        />
        {value && value.pattern !== 'yearly' && value.pattern !== 'other' && frequency !== 'weekdays' && (
          <>
            <Label htmlFor={ids.interval} className="font-normal">
              {t('webmail.calendarUx.recurrence.every')}
            </Label>
            <Input id={ids.interval} type="number" min={1} max={99} className="w-20" value={value.interval} onChange={(e) => set({ interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })} />
            <span className="text-sm">{t(unitKey[value.pattern], { n: value.interval })}</span>
          </>
        )}
      </div>
      {value?.pattern === 'weekly' && frequency !== 'weekdays' && (
        <div role="group" aria-label={t('webmail.calendarUx.recurrence.onDays')} className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((_, i) => {
            const d = WEEKDAYS[(format.weekStart + i) % 7] ?? 'monday';
            const on = value.daysOfWeek?.includes(d) ?? false;
            return (
              <Button
                key={d}
                type="button"
                size="sm"
                variant="outline"
                aria-pressed={on}
                aria-label={dayName(d)}
                className={on ? 'border-primary bg-primary/10 text-primary hover:bg-primary/15' : undefined}
                onClick={() => {
                  const next = on ? (value.daysOfWeek ?? []).filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || value.daysOfWeek?.includes(x));
                  if (next.length > 0) set({ daysOfWeek: next });
                }}
              >
                {dayName(d).slice(0, 2)}
              </Button>
            );
          })}
        </div>
      )}
      {value?.pattern === 'monthly' && (
        <SegmentedControl<MonthlyMode>
          label={t('webmail.calendarUx.recurrence.monthlyOn')}
          value={monthlyModeOf(value)}
          options={[
            { value: 'date', label: t('webmail.calendarUx.recurrence.onDay', { day: start.getDate() }) },
            ...(nth !== 'last' ? [{ value: 'nth' as const, label: t('webmail.calendarUx.recurrence.onNth', { index: index(nth), day: dayName(day) }) }] : []),
            ...(nthOf(start, true) === 'last' ? [{ value: 'last' as const, label: t('webmail.calendarUx.recurrence.onNth', { index: index('last'), day: dayName(day) }) }] : []),
            { value: 'lastDay', label: t('webmail.calendarUx.recurrence.onLastDay') },
          ]}
          onChange={(mode) =>
            onChange({
              pattern: 'monthly',
              interval: value.interval,
              startDate: value.startDate,
              ...(value.endDate && { endDate: value.endDate }),
              ...(value.count && { count: value.count }),
              ...(mode === 'date' ? { dayOfMonth: start.getDate() } : mode === 'lastDay' ? { weekIndex: 'last', dayKind: 'day' } : { weekIndex: mode === 'last' ? 'last' : nth, daysOfWeek: [day] }),
            })
          }
        />
      )}
      {value?.pattern === 'yearly' && (
        <SegmentedControl<'date' | 'nth'>
          label={t('webmail.calendarUx.recurrence.yearlyOn')}
          value={value.weekIndex ? 'nth' : 'date'}
          options={[
            { value: 'date', label: t('webmail.calendarUx.recurrence.onDate', { date: format.dayMonth(start) }) },
            { value: 'nth', label: t('webmail.calendarUx.recurrence.onNthOf', { index: index(nth), day: dayName(day), month: monthName }) },
          ]}
          onChange={(mode) =>
            onChange({
              pattern: 'yearly',
              interval: 1,
              month: start.getMonth() + 1,
              startDate: value.startDate,
              ...(value.endDate && { endDate: value.endDate }),
              ...(value.count && { count: value.count }),
              ...(mode === 'date' ? { dayOfMonth: start.getDate() } : { weekIndex: nth, daysOfWeek: [day] }),
            })
          }
        />
      )}
      {value && (
        <div className="flex flex-wrap items-end gap-3">
          <SegmentedControl<EndMode>
            label={t('webmail.calendarUx.recurrence.ends')}
            value={endMode}
            options={[
              { value: 'never', label: t('webmail.calendarUx.recurrence.endsNever') },
              { value: 'date', label: t('webmail.calendarUx.recurrence.endsOn') },
              { value: 'count', label: t('webmail.calendarUx.recurrence.endsAfter') },
            ]}
            onChange={(mode) => {
              const rest = withoutEnd(value);
              onChange(mode === 'never' ? rest : mode === 'date' ? { ...rest, endDate: isoDate(new Date(start.getFullYear(), start.getMonth() + 3, start.getDate())) } : { ...rest, count: 10 });
            }}
          />
          {endMode === 'date' && (
            <DateTimePicker aria-label={t('webmail.calendarUx.recurrence.endDate')} mode="date" weekStartsOn={format.weekStart} weekTimeZone={format.weekTimeZone} className="w-40" value={value.endDate ?? ''} min={isoDate(start)} onValueChange={(next) => next && set({ endDate: next })} />
          )}
          {endMode === 'count' && (
            <div className="flex items-center gap-2">
              <Input id={ids.count} aria-label={t('webmail.calendarUx.recurrence.countLabel')} type="number" min={1} max={999} className="w-20" value={value.count ?? 1} onChange={(e) => set({ count: Math.max(1, Math.min(999, Number(e.target.value) || 1)) })} />
              <span className="text-sm">{t('webmail.calendarUx.recurrence.times', { n: value.count ?? 1 })}</span>
            </div>
          )}
        </div>
      )}
      {value && <p className="text-xs text-muted-foreground" aria-live="polite">{recurrenceText(value, t, format)}</p>}
    </div>
  );
}
